import type { NumericKeys } from '@seaber/common/lib/generics';
import { useDefaultCurrency } from '../../../state/zustand';
import { TableCell, TableRow, Text } from '../../ui';
import { formatCurrency } from '../../../lib/format';
import React from 'react';
import type { SuggestionWithMetrics } from './SuggestionEstimator';
import type { Currency } from '@seaber/common/graphql/types';

type ValueRowProps =
  | ({
      valueKey: NumericKeys<SuggestionWithMetrics>;
      getValue?: never;
    } & CommonValueRowProps)
  | ({
      valueKey?: never;
      getValue: (s: SuggestionWithMetrics) => number;
    } & CommonValueRowProps);
type CommonValueRowProps = {
  label: string;
  suffix?: string;
  isBold?: boolean;
  suggestions?: SuggestionWithMetrics[];
  isCurrency?: boolean;
  decimals?: number;
};

export const ValueRow = ({
  label,
  suffix = '',
  isBold = true,
  suggestions = [],
  valueKey,
  getValue,
  isCurrency = true,
  decimals = 0,
}: ValueRowProps) => {
  const defaultCurrency = useDefaultCurrency();
  const values = suggestions.map((s) => {
    const value =
      (valueKey && s[valueKey]) ?? (getValue && getValue(s)) ?? null;
    const currency = defaultCurrency;
    return { value, currency };
  });

  return (
    <TableRow
      style={{ whiteSpace: 'nowrap' }}
      data-testid={`suggestion-estimator-row-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <TableCell>
        <Text fontWeight={isBold ? 'bold' : 'normal'}>{label}</Text>
      </TableCell>
      {values.map(({ currency, value }, i) => (
        <TableCell key={`${label.toLowerCase().replace(/\s+/g, '-')}-${i}`}>
          <ValueCell
            value={value}
            currency={currency}
            isCurrency={isCurrency}
            decimals={decimals}
            suffix={suffix}
          />
        </TableCell>
      ))}
    </TableRow>
  );
};

const ValueCell = ({
  value,
  currency,
  isCurrency = true,
  decimals = 0,
  suffix = '',
}: {
  value: number | null;
  currency: Currency;
  isCurrency?: boolean;
  decimals?: number;
  suffix?: string;
}) => {
  if (value === null) return <Text>N/A</Text>;

  const formattedValue = isCurrency
    ? formatCurrency(value, decimals, currency)
    : value.toFixed(decimals);
  return (
    <Text>
      {formattedValue} {suffix && ` ${suffix}`}
    </Text>
  );
};

export const TextRow = ({
  label,
  values,
  isBold = true,
  rowTestId,
}: {
  label: string;
  values: Array<string>;
  isBold?: boolean;
  rowTestId?: string;
}) => (
  <TableRow style={{ whiteSpace: 'nowrap' }} data-testid={rowTestId}>
    <TableCell>
      <Text fontWeight={isBold ? 'bold' : 'normal'}>{label}</Text>
    </TableCell>
    {values.map((value, i) => (
      <TableCell key={`${label.toLowerCase().replace(/\s+/g, '-')}-${i}`}>
        {value}
      </TableCell>
    ))}
  </TableRow>
);
